import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import { ICoctailsState, IUploadedMedia } from './interfaces';
import api from '../../lib/api';
import { AppState } from '..';
import { ICoctail } from '../../types';
import { isAxiosError } from 'axios';

const initialState: ICoctailsState = {
  loading: false,
  coctails: [],
  coctail: {
    data: {
      _id: '',
      eng: undefined,
      ru: undefined,
    },
    loading: false,
  },
  media: null,
};

export const coctailsSlice = createSlice({
  name: 'coctails',
  initialState,
  reducers: {},
  extraReducers: builder =>
    builder
      // получение списка коклейлей
      .addCase(fetchCoctails.pending, state => {
        state.loading = true;
      })
      .addCase(fetchCoctails.fulfilled, (state, { payload }) => {
        const { data } = payload;
        state.coctails = data;
        state.loading = false;
      })
      .addCase(fetchCoctails.rejected, state => {
        state.loading = false;
      })
      // загрузка картинок и видео
      .addCase(uploadMedia.pending, state => {
        state.loading = true;
      })
      .addCase(uploadMedia.fulfilled, (state, {}) => {
        state.loading = false;
      })
      .addCase(uploadMedia.rejected, state => {
        state.loading = false;
      })
      // получение списка картинок или видео
      .addCase(fetchMedia.pending, state => {
        state.loading = true;
      })
      .addCase(fetchMedia.fulfilled, (state, { payload }) => {
        state.media = payload.data.data;
        state.loading = false;
      })
      .addCase(fetchMedia.rejected, state => {
        state.loading = false;
      })
      // создание коклейля
      .addCase(createCoctail.pending, state => {
        state.loading = true;
      })
      .addCase(createCoctail.fulfilled, (state, { payload }) => {
        const { data } = payload;
        state.coctail = data;
        state.loading = false;
      })
      .addCase(createCoctail.rejected, state => {
        state.loading = false;
      })
      // получение коктейля по id
      .addCase(fetchCoctail.pending, state => {
        state.coctail.loading = true;
      })
      .addCase(fetchCoctail.fulfilled, (state, { payload }) => {
        const { data } = payload;
        state.coctail.data = data;
        state.coctail.loading = false;
      })
      .addCase(fetchCoctail.rejected, state => {
        state.coctail.loading = false;
      })
      // редактирование коктейля
      .addCase(editCoctail.pending, state => {
        state.coctail.loading = true;
      })
      .addCase(editCoctail.fulfilled, (state, {}) => {
        state.coctail.loading = false;
      })
      .addCase(editCoctail.rejected, state => {
        state.coctail.loading = false;
      }),
});

// ------------------------------------ THUNKS ------------------------------------ //

// получение списка коклейлей
export const fetchCoctails = createAsyncThunk<any, any, any>(
  'coctails/fetchCoctails',
  async (_, thunkApi) => {
    try {
      const response = await api.coctails.getCoctails();
      const coctails = response.map((coctail: ICoctail) => {
        return coctail;
      });
      return { data: coctails };
    } catch (err) {
      console.log('uploadMedia err', err);
      return thunkApi.rejectWithValue({ err });
    }
  },
);

// загрузка картинок и видео
export const uploadMedia = createAsyncThunk<any, any, any>(
  'coctails/uploadMedia',
  async (params, thunkApi) => {
    try {
      const response: IUploadedMedia = await api.coctails.uploadMedia(params);
      return { data: response };
    } catch (err) {
      console.log('uploadMedia err', err);
      return thunkApi.rejectWithValue({ err });
    }
  },
);

// удаление картинок и видео
export const deleteMedia = createAsyncThunk<any, any, any>(
  'coctails/deleteMedia',
  async (params, thunkApi) => {
    try {
      const response: IUploadedMedia = await api.coctails.deleteMedia(params.name, params.type);
      return { data: response };
    } catch (err) {
      console.log('deleteMedia err', err);
      return thunkApi.rejectWithValue({ err });
    }
  },
);

// получение всех картинок и видео
export const fetchMedia = createAsyncThunk<any, any, any>(
  'coctails/getMedia',
  async (type, thunkApi) => {
    try {
      const response: IUploadedMedia = await api.coctails.getMedia(type);
      return { data: response };
    } catch (err) {
      console.log('getMedia err', err);
      return thunkApi.rejectWithValue({ err });
    }
  },
);

// получение коктейля по id
export const fetchCoctail = createAsyncThunk<any, any, any>(
  'coctails/fetchCoctail',
  async (params, thunkApi) => {
    const { id } = params;
    try {
      const response = await api.coctails.getCoctailById(id);

      return { data: response };
    } catch (err) {
      console.log('fetchCoctail err', err);
      return thunkApi.rejectWithValue({ err });
    }
  },
);

//создание коклейля
export const createCoctail = createAsyncThunk<any, any, any>(
  'coctails/createCoctail',
  async (coctail, thunkApi) => {
    try {
      const response = await api.coctails.createCoctails(coctail);
      return { data: response };
    } catch (err) {
      console.log('createCoctail err', err);
      return thunkApi.rejectWithValue({ err });
    }
  },
);

// редактирование коктейля
export const editCoctail = createAsyncThunk<any, any, any>(
  'coctails/editCoctail',
  async (params, thunkApi) => {
    const { coctail, id } = params;
    try {
      const response = await api.coctails.editCoctail(coctail, id);
      await thunkApi.dispatch(fetchCoctail({ id }));
      return { data: response };
    } catch (err) {
      console.log('editCoctail err', err);
      return thunkApi.rejectWithValue({ err });
    }
  },
);

// удаление коктейля
export const deleteCoctail = async (id: string) => {
  try {
    await api.coctails.deleteCoctail(id);
  } catch (e) {
    return e.response.status;
  }
};

// ------------------------------------ SELECTORS ------------------------------------ //
// cелектор для получения коктейля по id
export const getCoctailById = createSelector(
  (state: ICoctailsState) => state,
  results => {
    return {
      data: results.coctail.data,
      loading: results.coctail.loading,
    };
  },
);

// селектор для получения списка коклейлей
export const getCoctailsList = createSelector(
  (state: ICoctailsState) => state,
  state => state,
);

// селектор для получения списка коклейлей
export const getMediaList = createSelector(
  (state: ICoctailsState) => state.media,
  state => state,
);

export const {} = coctailsSlice.actions;

export const selectCoctailsState = (state: AppState) => state.coctailsReducer;

export default coctailsSlice.reducer;
